import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Spinner, Button, Input, Textarea, Switch, Chip } from '@nextui-org/react';
import {
  ArrowLeft, Save, MapPin, Plus, Trash2, Upload, Image as ImageIcon,
  Phone, Mail, Link2, X,
} from 'lucide-react';
import shopService from '../services/shopService';
import type { Shop, ShopAddress, SocialMediaCollection } from '../services/shopService';

const TEAL = '#3FCED2';

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const EditShopPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shop, setShop] = useState<Shop | null>(null);
  const [changed, setChanged] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [shopLink, setShopLink] = useState('');
  const [localDartTesting, setLocalDartTesting] = useState(false);
  const [localPointChange, setLocalPointChange] = useState(false);
  const [address, setAddress] = useState<ShopAddress | null>(null);
  const [socialMedia, setSocialMedia] = useState<SocialMediaCollection>({});

  const fetchShop = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await shopService.getShopById(id);
      const s = (res as any)?.data ?? res;
      if (!s) { setMessage({ type: 'error', text: 'Shop konnte nicht geladen werden' }); return; }
      setShop(s);
      setName(s.name ?? '');
      setEmail(s.email ?? '');
      setPhone(s.phone ?? '');
      setDescription(s.description ?? '');
      setShopLink(s.shopLink ?? '');
      setLocalDartTesting(s.localDartTesting ?? false);
      setLocalPointChange(s.localPointChange ?? false);
      setAddress(s.address ?? null);
      setSocialMedia(s.socialMediaCollection ?? {});
      setChanged(false);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Shop konnte nicht geladen werden' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchShop(); window.scrollTo({ top: 0 }); }, [fetchShop]);
  const markChanged = () => setChanged(true);

  const handleSave = async () => {
    if (!name.trim()) { setMessage({ type: 'error', text: 'Bitte einen Namen festlegen' }); return; }
    if (!shop?._id && !shop?.id) return;
    setSaving(true); setMessage(null);
    try {
      await shopService.updateShop({
        id: shop._id ?? shop.id,
        name,
        email: email.trim() === '' ? null : email,
        phone: phone.trim() === '' ? null : phone,
        description: description.trim() === '' ? null : description,
        shopLink: shopLink.trim() === '' ? null : shopLink,
        localDartTesting,
        localPointChange,
        addressCountry: address?.country ?? null,
        addressLabel: address?.label ?? null,
        addressLatitude: address?.latitude ?? null,
        addressLongitude: address?.longitude ?? null,
        socialMediaCollection: socialMedia,
      });
      setChanged(false);
      setMessage({ type: 'success', text: 'Erfolgreich aktualisiert' });
      await fetchShop();
    } catch (err: any) {
      console.error(err);
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Aktualisierung fehlgeschlagen' });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (changed && !window.confirm('Deine Änderungen wurden noch nicht gespeichert. Wirklich abbrechen?')) return;
    navigate('/verwaltung?tab=shops');
  };

  const handleFileUpload = async (field: 'logo' | 'image', file: File) => {
    if (!shop?._id && !shop?.id) return;
    if (file.size > 20 * 1024 * 1024) { setMessage({ type: 'error', text: 'Datei zu groß (Max. 20 MB)' }); return; }
    if (field === 'logo' && !file.name.toLowerCase().endsWith('.png')) {
      setMessage({ type: 'error', text: 'Logo muss eine .png Datei sein' }); return;
    }
    setSaving(true);
    try {
      const base64 = await fileToBase64(file);
      await shopService.updateShop({ id: shop._id ?? shop.id, [field]: base64 });
      await fetchShop();
      setMessage({ type: 'success', text: `${field === 'logo' ? 'Logo' : 'Header'} aktualisiert` });
    } catch { setMessage({ type: 'error', text: 'Upload fehlgeschlagen' }); }
    finally { setSaving(false); }
  };

  const handleFileRemove = async (field: 'logo' | 'image') => {
    if (!shop?._id && !shop?.id) return;
    if (!window.confirm('Wirklich entfernen?')) return;
    setSaving(true);
    try {
      await shopService.updateShop({ id: shop._id ?? shop.id, [field]: null });
      await fetchShop();
      setMessage({ type: 'success', text: 'Erfolgreich entfernt' });
    } catch { setMessage({ type: 'error', text: 'Entfernen fehlgeschlagen' }); }
    finally { setSaving(false); }
  };

  const handleAddImage = async (file: File) => {
    if (!shop?._id && !shop?.id) return;
    if (file.size > 20 * 1024 * 1024) { setMessage({ type: 'error', text: 'Datei zu groß (Max. 20 MB)' }); return; }
    setSaving(true);
    try {
      const base64 = await fileToBase64(file);
      await shopService.addImage((shop._id ?? shop.id) as string, base64);
      await fetchShop();
      setMessage({ type: 'success', text: 'Bild hinzugefügt' });
    } catch { setMessage({ type: 'error', text: 'Bild konnte nicht hochgeladen werden' }); }
    finally { setSaving(false); }
  };

  const handleRemoveImage = async (img: string) => {
    if (!shop?._id && !shop?.id) return;
    if (!window.confirm('Bild wirklich entfernen?')) return;
    setSaving(true);
    try {
      await shopService.removeImage((shop._id ?? shop.id) as string, img);
      await fetchShop();
    } catch { setMessage({ type: 'error', text: 'Bild konnte nicht entfernt werden' }); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <Spinner color="primary" size="lg" label="Shop laden..." /></div>;
  }
  if (!shop) {
    return <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-4">
      <p className="text-zinc-400 mb-4">Shop konnte nicht geladen werden.</p>
      <Button onPress={handleBack} startContent={<ArrowLeft size={16} />}>Zurück</Button>
    </div>;
  }

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Helmet><title>{name || 'Shop'} bearbeiten - DartCloud</title></Helmet>

      <div className="sticky top-0 z-10 bg-black/80 backdrop-blur border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={handleBack} className="text-zinc-300 hover:text-white p-1"><ArrowLeft size={20} /></button>
          <h1 className="text-lg font-bold flex-1 truncate">Shop bearbeiten</h1>
          {changed && <Chip size="sm" color="warning" variant="flat">Ungespeichert</Chip>}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-8">
        {message && (
          <div className={`rounded-xl p-3 text-sm flex items-start gap-2 ${
            message.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-300' : 'bg-red-500/10 border border-red-500/30 text-red-300'
          }`}>
            <span className="flex-1">{message.text}</span>
            <button onClick={() => setMessage(null)}><X size={14} /></button>
          </div>
        )}

        <section>
          <h2 className="text-base font-semibold mb-2">Name</h2>
          <Input value={name} onValueChange={(v) => { setName(v); markChanged(); }} placeholder="Name"
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Services</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">Lokales Dart-Testen möglich</span>
              <Switch isSelected={localDartTesting} onValueChange={(v) => { setLocalDartTesting(v); markChanged(); }} />
            </div>
            <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
              <span className="text-sm">Lokaler Spitzentausch</span>
              <Switch isSelected={localPointChange} onValueChange={(v) => { setLocalPointChange(v); markChanged(); }} />
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Online-Shop Link</h2>
          <Input value={shopLink} onValueChange={(v) => { setShopLink(v); markChanged(); }}
            placeholder="https://..." startContent={<Link2 size={16} className="text-zinc-400" />}
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Kontakt</h2>
          <div className="space-y-3">
            <Input value={email} onValueChange={(v) => { setEmail(v); markChanged(); }} placeholder="Email" type="email"
              startContent={<Mail size={16} className="text-zinc-400" />}
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            <Input value={phone} onValueChange={(v) => { setPhone(v); markChanged(); }} placeholder="Telefon"
              startContent={<Phone size={16} className="text-zinc-400" />}
              classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Anfahrt</h2>
          {address ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-3 flex items-center gap-3">
              <MapPin size={18} className="text-zinc-400 flex-shrink-0" />
              <span className="flex-1 text-sm truncate">{address.label || 'Keine Adresse'}</span>
              <Button isIconOnly size="sm" variant="light" color="danger"
                onPress={() => { setAddress(null); markChanged(); }}><Trash2 size={14} /></Button>
            </div>
          ) : (
            <p className="text-sm text-zinc-400">Keine Adresse hinterlegt. Adressen können aktuell nur in der Mobile-App geändert werden.</p>
          )}
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Beschreibung</h2>
          <Textarea value={description} onValueChange={(v) => { setDescription(v); markChanged(); }}
            placeholder="Beschreibung deines Shops" minRows={6}
            classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
        </section>

        <section>
          <h2 className="text-base font-semibold mb-2">Social Media</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(['website', 'instagram', 'facebook', 'twitter', 'tiktok', 'youtube'] as const).map((k) => (
              <Input key={k} value={(socialMedia as any)[k] ?? ''}
                onValueChange={(v) => { setSocialMedia({ ...socialMedia, [k]: v }); markChanged(); }}
                placeholder={k.charAt(0).toUpperCase() + k.slice(1)}
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-800' }} />
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-1">Logo</h2>
          <p className="text-xs text-zinc-400 mb-3">Logo deines Shops (.png)</p>
          {shop.logo && (
            <div className="mb-3">
              <div className="h-32 bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden flex items-center justify-center">
                <img src={shopService.buildFileUrl(shop.logo) ?? ''} alt="Logo" className="h-full object-contain" />
              </div>
              <Button size="sm" variant="light" color="danger" className="mt-2" onPress={() => handleFileRemove('logo')}
                startContent={<Trash2 size={14} />}>Logo entfernen</Button>
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 rounded-xl cursor-pointer text-sm">
            <Upload size={14} /> Logo hochladen
            <input type="file" accept="image/png" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload('logo', f); e.target.value = ''; }} />
          </label>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-1">Header-Bild</h2>
          {shop.image && (
            <div className="mb-3">
              <div className="h-40 bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
                <img src={shopService.buildFileUrl(shop.image) ?? ''} alt="Header" className="w-full h-full object-cover" />
              </div>
              <Button size="sm" variant="light" color="danger" className="mt-2" onPress={() => handleFileRemove('image')}
                startContent={<Trash2 size={14} />}>Header entfernen</Button>
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 rounded-xl cursor-pointer text-sm">
            <Upload size={14} /> Header hochladen
            <input type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload('image', f); e.target.value = ''; }} />
          </label>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-3">Bilder</h2>
          {(!shop.images || shop.images.length === 0) ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-sm text-zinc-400 flex items-center gap-2">
              <ImageIcon size={16} /> Keine Bilder
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {shop.images.map((img) => (
                <div key={img} className="relative aspect-square rounded-xl overflow-hidden border border-zinc-800 group">
                  <img src={shopService.buildFileUrl(img) ?? ''} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => handleRemoveImage(img)}
                    className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 mt-3 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 rounded-xl cursor-pointer text-sm">
            <Plus size={14} /> Bild hinzufügen
            <input type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAddImage(f); e.target.value = ''; }} />
          </label>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur border-t border-zinc-800 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
          <Button className="w-full" isDisabled={!changed || saving} isLoading={saving} onPress={handleSave}
            startContent={!saving ? <Save size={16} /> : null}
            style={{ backgroundColor: TEAL, color: '#000' }}>Aktualisieren</Button>
        </div>
      </div>
    </div>
  );
};

export default EditShopPage;
